/**
 * Transcript vocabulary and entry schema (architecture.md §7.3).
 *
 * @remarks
 * The single source of truth for transcript source/priority tags and the
 * structured entry shape stored in `RuntimeState.session` — narrative-effect
 * schemas ({@link ../effects.ts | effects}) and the runtime-state schemas both
 * consume it, and `@orpheus/engine-runtime` re-exports these types instead of
 * declaring its own (Mike's R1 ruling, 2026-08-12: schemas own the shapes; the
 * engine keeps the pure helpers). The UI styles by source and derives
 * screen-reader announcement behavior from priority; the engine never emits
 * presentation markup.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { audioCuesSchema } from './audio.js';
import { conditionalTextSchema } from './conditional-text.js';
import { speakerIdSchema } from './identifiers.js';
import { nonBlankStringSchema, nonNegativeIntegerSchema } from './primitives.js';

/**
 * The ENGINE's four sources — the ones it speaks in itself: the world's
 * answers, a found document, the player's echo, the terminal's chrome. Fixed
 * because the engine's own built-ins and the shell's own registers key on
 * them. Everything else that speaks is a SPEAKER the pack declares (D192,
 * `speakers.ts`); until then the schema named one, `eurydice`, in this enum.
 */
export const fixedTranscriptSourceSchema = z.enum(['environment', 'human_log', 'player', 'system']);

/** Every fixed source, in canonical (alphabetical) order. */
export const FIXED_TRANSCRIPT_SOURCES = fixedTranscriptSourceSchema.options;

/** One of {@link FIXED_TRANSCRIPT_SOURCES}. */
export type FixedTranscriptSource = z.infer<typeof fixedTranscriptSourceSchema>;

/**
 * Who is "speaking" a transcript entry — the UI's styling key: one of the
 * engine's fixed sources, or a declared speaker's id (D192). The loader
 * refuses a speaker id no `speakers` entry declares.
 */
export const transcriptSourceSchema = z.union([fixedTranscriptSourceSchema, speakerIdSchema]);

/** Announcement/styling priorities, lowest to highest urgency: ambient < standard < critical. */
export const transcriptPrioritySchema = z.enum(['ambient', 'critical', 'standard']);

/** Every known {@link TranscriptPriority}, in canonical (alphabetical) order. */
export const TRANSCRIPT_PRIORITIES = transcriptPrioritySchema.options;

/** One of {@link TRANSCRIPT_PRIORITIES}. */
export type TranscriptPriority = z.infer<typeof transcriptPrioritySchema>;

/** A fixed source or a speaker id — see {@link transcriptSourceSchema}. */
export type TranscriptSource = z.infer<typeof transcriptSourceSchema>;

/**
 * Whether a source is one of the engine's fixed four — the narrowing every
 * reader keyed on the fixed kinds needs before it indexes a table by source.
 *
 * @param source - Any transcript source
 * @returns Whether it is fixed (and therefore not a speaker id)
 */
export const isFixedTranscriptSource = (source: TranscriptSource): source is FixedTranscriptSource =>
	(FIXED_TRANSCRIPT_SOURCES as readonly string[]).includes(source);

/**
 * One structured transcript entry, as stored in `RuntimeState.session` and
 * rendered by `player-ui`. The `id` is deterministically derived — never
 * random — so replays and saves stay byte-for-byte reproducible.
 */
export const runtimeTranscriptEntrySchema = z
	.strictObject({
		/**
		 * D264: the sounds the line made, carried with the words that caption
		 * them. Stamped once, with the text; a host reads them only from the
		 * entries of the turn it has just run (`TurnOutcome.cues`), so a
		 * restored or exported transcript is never heard a second time.
		 */
		cues: audioCuesSchema.optional(),
		id: nonBlankStringSchema,
		/**
		 * D86 (Calder's export contract §3.4): the answers a which-one ask
		 * OFFERED, resolved at ask time in the parser's presented order — the
		 * same discipline as the parked `questionText`, so the record and the
		 * surface cannot disagree. Present only on a which-one ask's entry;
		 * the transcript export composes them into the question line when the
		 * line does not already name them (the candidates leave
		 * `session.pendingQuestion` the moment the question is answered, so a
		 * record without this field shows "a question with no options and an
		 * answer to nothing"). Since D229 (E14) the ask's own text names them
		 * too, through the slot's `{options}`; this field stays the structured
		 * record of what was offered. Two or more always, by the same law as
		 * the question schema's candidates. Optional on a strict object:
		 * pre-D86 saves validate unchanged.
		 */
		offeredAnswers: z.array(nonBlankStringSchema).min(2).readonly().optional(),
		priority: transcriptPrioritySchema,
		source: transcriptSourceSchema,
		text: nonBlankStringSchema,
		triggerContext: nonBlankStringSchema.optional(),
		turnCount: nonNegativeIntegerSchema,
	})
	.readonly();

/** One structured transcript entry — see {@link runtimeTranscriptEntrySchema}. */
export type RuntimeTranscriptEntry = z.infer<typeof runtimeTranscriptEntrySchema>;

/**
 * The field trio every AUTHORED narrative line carries. Shared between
 * {@link narrativeLineSchema} and the `append_narrative` effect (DRY —
 * engineering_standards §13.1); not part of the package's public API.
 */
export const narrativeLineFieldSchemas = {
	/**
	 * D264: the sounds this line makes when it is shown, in play order. The
	 * text may be conditional; the cues are not — they play for every
	 * reading, so two readings that sound different are two lines (the
	 * conditional-narratives form).
	 */
	cues: audioCuesSchema.optional(),
	priority: transcriptPrioritySchema.default('standard'),
	source: transcriptSourceSchema,
	/**
	 * D74: authored text is CONDITIONAL TEXT everywhere — a plain string (the
	 * overwhelming common case, unchanged) or D55's fallback+variants form.
	 * The blind-EURYDICE register asked why only descriptions and voice pools
	 * could switch readings; the honest answer was "no reason" — conditional
	 * text is the primitive, and a primitive available in two blessed fields
	 * is two mechanisms pretending to be one. Transcript ENTRIES stay plain
	 * strings: resolution happens once, at the stamping boundary.
	 */
	text: conditionalTextSchema,
};

/**
 * One authored narrative line: what is said, on which channel, at what
 * priority (defaults to `standard`). The content-side counterpart of a
 * transcript entry — the engine stamps `id`/`turnCount` when it renders one.
 * Added in the R1.1 amendment (2026-08-12): room first-visit beats, use
 * interactions, lock refusals, and ask responses all speak in these, because
 * the POC design fires most beats on two or three channels at once
 * (`docs/design/vertical-slice-content-design.md` §6).
 */
export const narrativeLineSchema = z.strictObject(narrativeLineFieldSchemas).readonly();

/** One authored narrative line — see {@link narrativeLineSchema}. */
export type NarrativeLine = z.infer<typeof narrativeLineSchema>;

/**
 * A non-empty run of authored lines — what every `*Narratives` field holds.
 *
 * @remarks
 * Named because D156 needed it in two places at once (the conditional-answer
 * primitive's fallback and its variants) and the repo already spelled it out
 * by hand in thirty. The other spellings are identical and can adopt this at
 * leisure; nothing about it is new.
 */
export const narrativeLinesSchema = z.array(narrativeLineSchema).min(1).readonly();
